<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useCatalogPage } from '@/composition/catalog/catalog-page-context';
  import V2CatalogCategoryNavList from './V2CatalogCategoryNavList.vue';

  /**
   * The categories rail (plan §5.7, tablet and desktop): «CATEGORÍAS» with
   * «Ordenar» and «Nueva categoría», a search from 15 categories, and the
   * table of contents. The active row stays in view by moving the rail's
   * own scroller (never `scrollIntoView`, which would scroll the page).
   */
  const SEARCH_FROM = 15;

  const {
    state,
    layout,
    data,
    access,
    url,
    commands,
    jumpToCategory,
  } = useCatalogPage();
  const root = ref<HTMLElement | null>(null);
  const head = ref<HTMLElement | null>(null);
  const query = ref('');

  const searchable = computed(() => data.sections.value.length > SEARCH_FROM);

  /** The rail's scroll container (the canvas's rail column). */
  function scroller(): HTMLElement | null {
    let element = root.value?.parentElement ?? null;
    while (element) {
      const overflow = window.getComputedStyle(element).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return element;
      element = element.parentElement;
    }
    return null;
  }

  watch(
    () => state.activeCategoryId,
    async (id) => {
      if (id === null) return;
      await nextTick();
      const box = scroller();
      const row = root.value?.querySelector<HTMLElement>(
        `[data-nav-id="${id}"]`
      );
      if (!box || !row) return;
      const top =
        row.getBoundingClientRect().top -
        box.getBoundingClientRect().top +
        box.scrollTop;
      const headHeight = head.value?.offsetHeight ?? 0;
      if (top - headHeight < box.scrollTop)
        box.scrollTop = top - headHeight - 8;
      else if (top + row.offsetHeight > box.scrollTop + box.clientHeight)
        box.scrollTop = top + row.offsetHeight - box.clientHeight;
    }
  );

  function newCategory() {
    url.openCategory('new', layout.columns.value.asideDocked);
  }
</script>

<template>
  <div ref="root" class="v2-catalog-rail" data-tier-only="tablet desktop">
    <div ref="head" class="rail-head">
      <div class="head-row">
        <h2 class="head-title">{{ $t('menuPage.nav.title') }}</h2>
        <template v-if="access.canManage && state.mode === 'browse'">
          <button
            v-if="data.canReorder.value"
            type="button"
            class="head-action"
            :aria-label="$t('menuPage.more.reorder')"
            :title="$t('menuPage.more.reorder')"
            @click="commands.startReorder(null)"
          >
            <i class="mdi mdi-swap-vertical" />
          </button>
          <button
            type="button"
            class="head-action"
            :aria-label="$t('menuPage.more.newCategory')"
            :title="$t('menuPage.more.newCategory')"
            @click="newCategory"
          >
            <i class="mdi mdi-plus" />
          </button>
        </template>
      </div>
      <PosUiInput
        v-if="searchable"
        v-model="query"
        class="head-search"
        icon="magnify"
        clearable
        :placeholder="$t('menuPage.nav.search')"
        :aria-label="$t('menuPage.nav.search')"
      />
    </div>
    <V2CatalogCategoryNavList
      class="rail-list"
      :query="query"
      @jump="jumpToCategory"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-rail {
    .rail-head {
      @apply tw-sticky tw-top-0 tw-bg-canvas tw-pb-1;
      z-index: 1;
    }

    .head-row {
      @apply tw-flex tw-h-11 tw-items-center tw-pl-3;
    }

    .head-title {
      @apply tw-m-0 tw-min-w-0 tw-flex-1 tw-truncate tw-text-xs tw-font-semibold tw-uppercase tw-text-neutral-500;
      letter-spacing: 0.06em;
    }

    .head-action {
      @apply tw-flex tw-h-11 tw-w-11 tw-flex-none tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-xl tw-text-neutral-500;

      &:hover {
        @apply tw-bg-neutral-900/5 tw-text-neutral-900;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .head-search {
      @apply tw-mb-1 tw-mt-1;
    }
  }
</style>
